import { DateTime } from "luxon";
import { getCompanyRegistry } from "./company/companyRegistrySingleton";

/**
 * Returns now in salon local time
 */
export function salonNow(): DateTime {
  return DateTime.utc().setZone(getCompanyRegistry().getSettings()?.timezone_name || "UTC");
}

/**
 * Format time for salon display (HH:mm).
 * Accepts either a UTC ISO string or a Luxon DateTime.
 */
export function fmtSalonHM(input: string | DateTime): string {
  const tz = getCompanyRegistry().getSettings()?.timezone_name || "UTC";
  const dt =
    typeof input === "string"
      ? DateTime.fromISO(input, { zone: "utc" }).setZone(tz)
      : input.setZone(tz);
  return dt.toFormat("HH:mm");
}

/**
 * Convert a salon-local date + time to a UTC ISO string (no milliseconds).
 * Ex: ("2025-10-18", "16:15", "Europe/Berlin") -> "2025-10-18T14:15:00Z"
 */
export function toUtcIsoFromSalon(
  dateISO: string, // "YYYY-MM-DD" (no time)
  timeHHmm: string, // "HH:mm" (24h)
  salonIanaTz: string // e.g. "Europe/Berlin"
): string {
  const dt = DateTime.fromISO(`${dateISO}T${timeHHmm}`, { zone: salonIanaTz });
  if (!dt.isValid) {
    throw new Error(`Invalid local datetime: ${dateISO} ${timeHHmm} (${salonIanaTz})`);
  }
  // Ensure explicit UTC with trailing 'Z' and no milliseconds to keep payload stable
   return dt.toUTC().toISO({ suppressMilliseconds: true });
 }

/** Format "HH:mm" from minutes since 00:00. */
export function hmFromMinutes(min: number): string {
  const h = Math.floor(min / 60);
  const m = min % 60;
  const hh = String(h).padStart(2, "0");
  const mm = String(m).padStart(2, "0");
  return `${hh}:${mm}`;
}

/** Minutes since 00:00 in salon TZ from a UTC ISO instant. */
export function minutesOfDayFromIsoSalon(isoUtc: string): number {
  const tz = getCompanyRegistry().getSettings()?.timezone_name || "UTC";
  const dt = DateTime.fromISO(isoUtc, { zone: "utc" }).setZone(tz);
  return dt.hour * 60 + dt.minute;
}

/**
 * Normalize any incoming ISO string to a UTC ISO string with 'Z'.
 * If the string has no offset, treat it as salon-local in the provided zone.
 */
export function ensureUtcIso(maybeIso: string, salonIanaTz: string): string {
  // If it already has an offset or Z, just normalize to Z in UTC.
  const hasOffset = /[Zz]|[+-]\d{2}:\d{2}$/.test(maybeIso);
  const dt = hasOffset
    ? DateTime.fromISO(maybeIso)
    : DateTime.fromISO(maybeIso, { zone: salonIanaTz });

  if (!dt.isValid) {
    throw new Error(`Invalid ISO datetime: ${maybeIso}`);
  }

  return dt.toUTC().toISO({ suppressMilliseconds: true });
}

/** Parse "13:45" picked in UI *on the appointment date* → ISO UTC (keeps stable format) */
export function localHmToUtc(dateYmd: string, hm: string): string {
  const tz = getCompanyRegistry().getSettings()?.timezone_name || "UTC";
  // ensure hm is "HH:mm"
  const hhmm = hm.length === 5 ? hm : hm.slice(0, 5);
  return toUtcIsoFromSalon(dateYmd, hhmm, tz);
}
